import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { Num } from './index';
import Modal, { BotaoSecundario, Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';

/* ============================================================================
   Perda / Roubo (modo perdido) - visual novo.

   Substitui pages/dashboard/dispositivos/components/modal/modal-change-travar.tsx.

   ISTO TRAVA OU DESTRAVA O APARELHO EM CAMPO. "Travar" poe o aparelho em modo
   perdido: ele para de ser usavel e passa a exibir a mensagem, o telefone, o
   e-mail e o endereco informados aqui. Se acionado por engano, quem estava
   trabalhando perde o telefone na hora.

   Contrato, o mesmo de antes:
     POST /dispositivos/perda_roubo { deviceId, tipo, mensagem, telefone, email, endereco }
     tipo: 'startLostModeParams' (travar) | 'stopLostModeParams' (destravar)

   AS DUAS VALIDACOES DO ORIGINAL FORAM MANTIDAS:
     - mensagem OBRIGATORIA quando o tipo e travar (e o texto que aparece na tela
       do aparelho - travar sem mensagem deixa quem achou o aparelho sem saber
       para quem devolver);
     - senha, se informada, no minimo 6 caracteres. (O campo de senha esta
       COMENTADO no formulario antigo e nao e enviado no corpo; a validacao
       ficou orfa la. Aqui ela nao foi reproduzida justamente por isso - nao ha
       campo de senha neste fluxo. Registrado para nao parecer esquecimento.)

   MUDANCAS:

   1. TRAVAR E DESTRAVAR DEIXARAM DE SER DOIS RADIOS IGUAIS. Eram duas opcoes do
      mesmo tamanho, e a diferenca entre elas e "o aparelho para de funcionar" vs
      "o aparelho volta". Agora sao dois blocos, com cor e consequencia escritas.

   2. Os quatro campos de contato SO APARECEM ao travar. Ao destravar eles nao
      tem efeito - o backend zera esses dados. Campo visivel que nao faz nada e
      onde o usuario perde tempo preenchendo.

   3. CONFIRMACAO nomeando o aparelho e dizendo o que vai acontecer.
   ============================================================================ */

type Device = {
  deviceId?: string;
  nome?: string;
  status?: number | string;
  perdaMensagem?: string;
  perdaTelefone?: string;
  perdaEmail?: string;
  perdaEndereco?: string;
};

export default function ModalPerdaRoubo({
  aberto,
  device,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  device: Device | null;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [tipo, setTipo] = React.useState<'startLostModeParams' | 'stopLostModeParams'>('startLostModeParams');
  const [mensagem, setMensagem] = React.useState('');
  const [telefone, setTelefone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [endereco, setEndereco] = React.useState('');
  const [confirmando, setConfirmando] = React.useState(false);
  const [enviando, setEnviando] = React.useState(false);

  React.useEffect(() => {
    if (!aberto) return;
    setTipo('startLostModeParams');
    setConfirmando(false);
    // Pre-carrega o que ja existe no registro, se vier na consulta - evita
    // redigitar a mensagem toda vez que se reaciona o modo perdido.
    setMensagem(device?.perdaMensagem ?? '');
    setTelefone(device?.perdaTelefone ?? '');
    setEmail(device?.perdaEmail ?? '');
    setEndereco(device?.perdaEndereco ?? '');
  }, [aberto, device]);

  const travando = tipo === 'startLostModeParams';

  // Mensagem obrigatoria ao TRAVAR - mesma regra do formulario antigo.
  const podeEnviar = travando ? mensagem.trim().length > 0 : true;

  async function enviar() {
    if (!device?.deviceId || !podeEnviar || enviando) return;
    setEnviando(true);
    try {
      await axios.post('/dispositivos/perda_roubo', {
        deviceId: device.deviceId,
        tipo,
        mensagem: travando ? mensagem.trim() : '',
        telefone: travando ? telefone.trim() : '',
        email: travando ? email.trim() : '',
        endereco: travando ? endereco.trim() : '',
      });

      // O Google ENFILEIRA o comando: aparelho offline so recebe quando voltar a ficar online.
      enqueueSnackbar(travando ? 'Comando enviado. O aparelho entra em modo perdido quando estiver online.' : 'Comando enviado. O aparelho sai do modo perdido quando estiver online.', { variant: 'success' });
      setConfirmando(false);
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length ? e.errors[0]?.message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Modal
        aberto={aberto && !confirmando}
        titulo="Perda / Roubo"
        descricao={device ? `${device.nome ?? ''} · ${device.deviceId ?? ''}` : undefined}
        icone="🔒"
        largura="max-w-[580px]"
        onFechar={onFechar}
        rodape={
          <>
            <BotaoSecundario onClick={onFechar} disabled={enviando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              disabled={!podeEnviar || enviando}
              className={cn(
                'h-9 rounded-lg px-4 text-[12.5px] font-bold text-white disabled:opacity-40',
                travando ? 'bg-danger' : 'bg-ok'
              )}
            >
              {travando ? 'Travar aparelho' : 'Destravar aparelho'}
            </button>
          </>
        }
      >
        {/* Dois blocos, nao dois radios do mesmo tamanho: a diferenca entre eles
            e "o aparelho para de funcionar" contra "o aparelho volta". */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setTipo('startLostModeParams')}
            className={cn(
              'rounded-lg border p-3 text-left',
              travando ? 'border-danger bg-danger-soft' : 'border-line hover:border-danger'
            )}
          >
            <span className={cn('block text-[12.5px] font-bold', travando && 'text-danger')}>Travar</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              Modo perdido: o aparelho deixa de ser usável e mostra seus contatos.
            </span>
          </button>

          <button
            type="button"
            onClick={() => setTipo('stopLostModeParams')}
            className={cn(
              'rounded-lg border p-3 text-left',
              !travando ? 'border-ok bg-ok-soft' : 'border-line hover:border-ok'
            )}
          >
            <span className={cn('block text-[12.5px] font-bold', !travando && 'text-ok')}>Destravar</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              Sai do modo perdido e volta ao uso normal. Os contatos são apagados.
            </span>
          </button>
        </div>

        {/* Os quatro campos so tem efeito ao travar - ao destravar o backend zera
            esses dados, e campo visivel que nao faz nada e onde se perde tempo. */}
        {travando ? (
          <>
            <Campo
              rotulo="Mensagem"
              obrigatorio
              dica="É o texto que aparece na tela do aparelho. Sem ela, quem achar o aparelho não sabe para quem devolver."
            >
              <textarea
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full rounded-lg border border-line bg-surface p-3 text-[12.5px] text-ink outline-none placeholder:text-faint focus:border-primary"
              />
            </Campo>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Campo rotulo="Telefone">
                <input
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  className={`${classesEntrada} tabular-nums`}
                />
              </Campo>
              <Campo rotulo="E-mail">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={classesEntrada}
                />
              </Campo>
            </div>

            <Campo rotulo="Endereço">
              <input value={endereco} onChange={(e) => setEndereco(e.target.value)} className={classesEntrada} />
            </Campo>
          </>
        ) : (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
            Ao destravar, a mensagem e os contatos de recuperação são apagados do registro e o aparelho volta
            ao uso normal.
          </div>
        )}
      </Modal>

      <Modal
        aberto={confirmando}
        titulo={travando ? 'Travar o aparelho?' : 'Destravar o aparelho?'}
        descricao={
          travando
            ? 'Quem estiver com o aparelho perde o uso assim que ele receber o comando (precisa estar online).'
            : 'O aparelho volta a ser utilizável e os contatos de recuperação são apagados.'
        }
        icone="!"
        largura="max-w-[500px]"
        onFechar={() => setConfirmando(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmando(false)} disabled={enviando}>
              Voltar
            </BotaoSecundario>
            <button
              type="button"
              onClick={enviar}
              disabled={enviando}
              className={cn(
                'h-9 rounded-lg px-4 text-[12.5px] font-bold text-white disabled:opacity-40',
                travando ? 'bg-danger' : 'bg-ok'
              )}
            >
              {enviando ? 'Enviando...' : travando ? 'Travar' : 'Destravar'}
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block font-semibold">{device?.nome || '—'}</span>
          <Num className="block text-[11px] text-muted-foreground">{device?.deviceId || '—'}</Num>

          {travando && (
            <div className="mt-2 border-t pt-2" style={{ borderColor: 'var(--gps-border)' }}>
              <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                Vai aparecer no aparelho
              </span>
              <span className="mt-0.5 block text-muted-foreground">{mensagem || '—'}</span>
              {(telefone || email || endereco) && (
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {[telefone, email, endereco].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
